/**
 * Shared helpers for order type name parsing and grouping.
 * Used by StartView (grouping flow) and ButtonLarge (pill display).
 */

const DELIMITER = ' - ';
const SUFFIX_FOR_HERE = ' for here';
const SUFFIX_TO_GO = ' to go';

/**
 * Get display name from order type (name or label).
 * @param {Object} ot - Order type object
 * @returns {string}
 */
export function getOrderTypeName(ot) {
  return (ot?.name || ot?.label || '').trim();
}

/**
 * Parse order type name into prefix and suffix for grouping/display.
 * Handles: "X - Y", "X For Here", "X To Go" (case-insensitive suffixes).
 * @param {string} name - Order type name
 * @returns {{ prefix: string|null, suffix: string|null }}
 */
export function parseOrderTypeName(name) {
  const n = (name || '').trim();
  if (!n) return { prefix: null, suffix: null };

  const idx = n.indexOf(DELIMITER);
  if (idx >= 0) {
    return {
      prefix: n.slice(0, idx).trim(),
      suffix: n.slice(idx + DELIMITER.length).trim()
    };
  }

  const lower = n.toLowerCase();
  if (lower.endsWith(SUFFIX_FOR_HERE)) {
    return {
      prefix: n.slice(0, -SUFFIX_FOR_HERE.length).trim(),
      suffix: 'For Here'
    };
  }
  if (lower.endsWith(SUFFIX_TO_GO)) {
    return {
      prefix: n.slice(0, -SUFFIX_TO_GO.length).trim(),
      suffix: 'To Go'
    };
  }

  return { prefix: null, suffix: null };
}

/**
 * Extract group prefix for an order type. Uses parseOrderTypeName first,
 * then falls back to word-based detection when name doesn't match known patterns.
 * @param {string} name - Order type name
 * @param {Array} allOrderTypes - All order types (for word-based fallback)
 * @param {Function} getName - (ot) => string
 * @returns {string|null}
 */
export function extractGroupPrefix(name, allOrderTypes = [], getName = getOrderTypeName) {
  const parsed = parseOrderTypeName(name);
  if (parsed.prefix) return parsed.prefix;

  const words = name.split(/\s+/);
  if (words.length < 2) return null;

  for (let n = words.length - 1; n >= 1; n--) {
    const candidate = words.slice(0, n).join(' ');
    const count = allOrderTypes.filter(t => {
      const n2 = getName(t);
      return n2 && (n2 === candidate || n2.startsWith(candidate + ' '));
    }).length;
    if (count >= 2) return candidate;
  }
  return null;
}

/**
 * Group order types by prefix. Returns groups (prefix with 2+ items) and otherItems.
 * @param {Array} orderTypes - Order types to group
 * @param {Function} getName - (ot) => string
 * @returns {{ groups: Array<{prefix: string, items: Array}>; otherItems: Array }}
 */
export function groupOrderTypesByPrefix(orderTypes, getName = getOrderTypeName) {
  const prefixToItems = {};
  const NO_PREFIX = '__NO_PREFIX__';

  orderTypes.forEach(ot => {
    const name = getName(ot);
    if (!name) return;

    const prefix = extractGroupPrefix(name, orderTypes, getName);
    const key = prefix || NO_PREFIX;
    if (!prefixToItems[key]) prefixToItems[key] = [];
    prefixToItems[key].push(ot);
  });

  const groups = [];
  const noPrefixItems = prefixToItems[NO_PREFIX] || [];

  Object.entries(prefixToItems).forEach(([key, items]) => {
    if (key !== NO_PREFIX && items.length >= 2) {
      groups.push({ prefix: key, items });
    }
  });
  groups.sort((a, b) => a.prefix.localeCompare(b.prefix));

  const otherItems = noPrefixItems.concat(
    ...Object.entries(prefixToItems)
      .filter(([k]) => k !== NO_PREFIX && prefixToItems[k].length < 2)
      .map(([, v]) => v)
  );

  return { groups, otherItems };
}

/**
 * Parse name for pill + main text display (ButtonLarge).
 * @param {string} name - Order type name
 * @returns {{ line1: string; line2: string|null }}
 */
export function parseNameForDisplay(name) {
  const { prefix, suffix } = parseOrderTypeName(name);
  if (prefix && suffix) return { line1: prefix, line2: suffix };
  return { line1: name || '', line2: null };
}
